import { useIframePoolStore } from "@/components/Canvas/IframePool/iframePoolStore";
import {
  StoryboardDelta,
  UpdateStoryboardsChange,
} from "@/store/zustand/changeLedgerTypes";
import {
  applyStoryboardDeltas,
  useIssuesStore,
} from "@/store/zustand/issues-store";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import {
  getStoryboardsForPage,
  usePagesStore,
} from "@/store/zustand/pagesStore";
import { toast } from "sonner";
import { useTextEditStore } from "../hooks/textEditStore";
import {
  fixUniquePathsForAllChildren,
  updateAllStoreLookups,
} from "./navTreeLookupUtils";
import { NavTreeNode } from "@/utils/types";
import { TempoElement } from "@/utils/tempoElement";
import { updateCodebaseId } from "./utils";
import { STORYBOARD_TYPES } from "@/utils/constants";
import groupBy from "lodash-es/groupBy";

const updateCodebaseIdsWithMap = (
  change: UpdateStoryboardsChange,
  newSbId: string,
) => {
  const codebaseIdHashingKey = change.changeFields.codebaseIdHashingKey;
  if (!codebaseIdHashingKey) {
    return;
  }

  if (
    !change.changeFields.createStoryboardFields ||
    !change.changeFields.createStoryboardFields.navTreeForInstantUpdate
  ) {
    return;
  }

  let newHtml = change.changeFields.createStoryboardFields.htmlForInstantUpdate;

  const updateCodebaseIds = (navTree: NavTreeNode) => {
    if (!navTree.tempoElement) {
      return;
    }

    const prevCodebaseId = navTree.tempoElement.codebaseId;
    let newCodebaseId;
    if (prevCodebaseId) {
      newCodebaseId = updateCodebaseId(prevCodebaseId, codebaseIdHashingKey);
      navTree.tempoElement = new TempoElement(
        newCodebaseId,
        newSbId,
        navTree.tempoElement.uniquePath,
      );
      newHtml = newHtml?.replace(
        new RegExp(prevCodebaseId, "g"),
        newCodebaseId,
      );
    }

    navTree.children?.forEach((child) => {
      updateCodebaseIds(child);
    });
  };

  updateCodebaseIds(
    change.changeFields.createStoryboardFields.navTreeForInstantUpdate,
  );

  change.changeFields.createStoryboardFields.htmlForInstantUpdate = newHtml;
};

const applyStoryboardDeltasToPage = (
  canvasId: string,
  deltas: StoryboardDelta[],
) => {
  // Instant pagesStore update
  const pagesStore = usePagesStore.getState();
  const pageDeltas = groupBy(deltas, (delta) => delta.pageId);

  // Undos are pushed and then reversed at once afterwards to be in the order they should be applied
  const allUndoDeltasReversed: StoryboardDelta[] = [];

  for (const [pageId, deltas] of Object.entries(pageDeltas)) {
    const page = pagesStore.pages[canvasId].find((p) => p.id === pageId);
    if (page && page.id === pageId) {
      const { newStoryboards, undoDeltas } = applyStoryboardDeltas(
        deltas,
        page.storyboards,
      );

      // Store undo deltas for potential undo
      allUndoDeltasReversed.push(...undoDeltas);

      // Update pagesStore instantly
      pagesStore.updatePage(canvasId, pageId, {
        storyboards: newStoryboards,
      });
    }
  }

  return allUndoDeltasReversed.reverse();
};

export const applyStoryboardsUpdate = async (
  change: UpdateStoryboardsChange,
) => {
  const canvasId = change.changeFields.canvasId;
  const subCanvas = useSupabaseStore.getState().subCanvases[canvasId];
  const mainCanvas = useIssuesStore.getState().currentCanvas[canvasId];

  if (!subCanvas && !mainCanvas) {
    toast.error("Canvas not found");
    return;
  }

  const supabaseClient = useSupabaseStore.getState().supabaseClient;
  if (!supabaseClient) {
    return;
  }

  const last_updated = new Date();

  const updateDict: Record<string, unknown> = {
    last_updated: last_updated.toISOString(),
  };

  if (change.changeFields.createStoryboardFields?.htmlForInstantUpdate) {
    const sbId = change.changeFields.deltas[0].storyboardId;

    updateCodebaseIdsWithMap(change, sbId);

    useIframePoolStore
      .getState()
      .setHtmlForStoryboard(
        sbId,
        change.changeFields.createStoryboardFields.htmlForInstantUpdate,
      );

    // If used default, don't update the nav tree to show a loading state
    if (change.changeFields.createStoryboardFields.navTreeForInstantUpdate) {
      const navTree =
        change.changeFields.createStoryboardFields.navTreeForInstantUpdate;

      fixUniquePathsForAllChildren(navTree);

      updateAllStoreLookups(
        sbId,
        {
          navTree,
        },
        true,
      );

      if (change.changeFields.createStoryboardFields.editTextAfterCreate) {
        useTextEditStore
          .getState()
          .requestTextEdit(
            sbId,
            navTree.domUniquePath ?? navTree.domUniquePathForComponent,
          );
      }
    }
  }

  // This is a sub-canvas
  if (subCanvas) {
    const newItem = {
      ...subCanvas,
      ...updateDict,
    };

    const { newStoryboards, undoDeltas } = applyStoryboardDeltas(
      change.changeFields.deltas,
      newItem.storyboards,
      change.changeFields.valuesAtStartOfAction,
    );
    newItem.storyboards = newStoryboards;
    change.instantUpdateData = {
      ...(change.instantUpdateData || {}),
      undoDeltas,
    };

    useSupabaseStore
      .getState()
      .onDbSnapshot("subCanvases", { newItems: [newItem] });

    await supabaseClient
      .from("sub_canvases")
      .update(updateDict)
      .eq("id", subCanvas.id);

    return;
  }

  const undoDeltas = applyStoryboardDeltasToPage(
    mainCanvas.id,
    change.changeFields.deltas,
  );

  change.instantUpdateData = {
    ...(change.instantUpdateData || {}),
    undoDeltas,
  };

  await useIssuesStore
    .getState()
    .updateCanvasStoryboardsInIssuesStore(
      mainCanvas.id,
      change.changeFields.deltas,
      updateDict,
      change,
    );
};

export const undoStoryboardsUpdate = async (
  change: UpdateStoryboardsChange,
) => {
  const undoDeltas = change.instantUpdateData?.undoDeltas;

  if (!undoDeltas) {
    return;
  }

  const canvasId = change.changeFields.canvasId;
  const subCanvas = useSupabaseStore.getState().subCanvases[canvasId];
  const mainCanvas = useIssuesStore.getState().currentCanvas[canvasId];

  if (!subCanvas && !mainCanvas) {
    toast.error("Canvas not found");
    return;
  }

  const supabaseClient = useSupabaseStore.getState().supabaseClient;
  if (!supabaseClient) {
    return;
  }

  const last_updated = new Date();

  const updateDict: Record<string, unknown> = {
    last_updated: last_updated.toISOString(),
  };

  // This is a sub-canvas
  if (subCanvas) {
    const newItem = {
      ...subCanvas,
      ...updateDict,
    };

    const { newStoryboards } = applyStoryboardDeltas(
      undoDeltas,
      newItem.storyboards,
    );
    newItem.storyboards = newStoryboards;

    useSupabaseStore
      .getState()
      .onDbSnapshot("subCanvases", { newItems: [newItem] });

    await supabaseClient
      .from("sub_canvases")
      .update(updateDict)
      .eq("id", subCanvas.id);

    return;
  }

  const pageUndoDeltas = applyStoryboardDeltasToPage(mainCanvas.id, undoDeltas);
  change.instantUpdateData = {
    ...(change.instantUpdateData || {}),
    undoDeltas: pageUndoDeltas,
  };

  await useIssuesStore
    .getState()
    .updateCanvasStoryboardsInIssuesStore(
      mainCanvas.id,
      undoDeltas,
      updateDict,
      undefined,
    );
};
